import React, { useState, ReactElement } from 'react';

import Modal61 from '@Common/Modal/Modal61';
import { ProjectLocation } from '@Types/ProjectLocation';
import { Dropdown } from 'primereact/dropdown';
import Notification from '@Common/Notification';

import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { actions as projectLocationActions } from '@Actions/projectLocationActions';
import * as types from '@Constants/actionTypes';

interface RemoveBaselineModalProps {
    modalRef: React.RefObject<any>;
    projectLocation: ProjectLocation;

    closeModal: (refresh?: boolean) => void;
}
export const RemoveBaselineModal = (props: RemoveBaselineModalProps): ReactElement => {
    const dispatch: Dispatch = useDispatch();
    const revokeBaselineFromProjectLocation = async (baselineId: number, locationId: number, projectId: number) =>
        await dispatch(projectLocationActions.revokeBaseline(baselineId, locationId, projectId) as any);

    const { modalRef, projectLocation, closeModal } = props;

    const [selectedBaselineId, setSelectedBaselineId] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const removeBaselineFromProjectLocation = async () => {
        setIsLoading(true);
        try {
            if (selectedBaselineId === null) {
                Notification('warning', 'Warning', 'Please select a baseline to remove.');
                return;
            }

            const response = await revokeBaselineFromProjectLocation(
                selectedBaselineId,
                projectLocation.locationId ?? 0,
                projectLocation.projectId ?? 0
            );

            if (response.type === types.REVOKE_BASELINE_PROJECTLOCATION_SUCCESS) {
                Notification('success', 'Success', 'Baseline removed successfully.');
                closeModal(true);
            } else {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal61
            ref={modalRef}
            isLoading={isLoading}
            title='Remove Baseline'
            primaryButtonText='Remove Baseline'
            secondaryButtonText='Cancel'
            body={
                <>
                    <div className='row mb-3'>
                        <div className='col-12'>
                            <div className='fs-5'>
                                Please select the baseline you wish to remove from the <strong>{projectLocation?.locationName}</strong> location.
                            </div>
                        </div>
                    </div>
                    <div className='row mb-3'>
                        <div className='col-12 d-flex align-items-center'>
                            <i className='fa-thin fa-triangle-exclamation text-warning fa-lg me-3'></i>
                            <span>
                                Only use this feature if a baseline was accidentally published to a location. This cannot be undone. All statuses,
                                files, comments, etc. will be lost. Do not use Remove Baseline to inactivate or retire the project from the location as
                                this will impact long term reporting and analytics.
                            </span>
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col-12'>
                            <Dropdown
                                className='w-100'
                                placeholder='Select Baseline'
                                value={selectedBaselineId}
                                options={projectLocation?.baselines ?? []}
                                onChange={(e) => setSelectedBaselineId(e.value)}
                                optionValue='id'
                                optionLabel='name'
                            />
                        </div>
                    </div>
                </>
            }
            onPrimaryClick={removeBaselineFromProjectLocation}
            onSecondaryClick={closeModal}
        />
    );
};
